import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { postRequest } from "@react/helpers/fetch"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"

const MODE_VISUALIZE = "visualize_data"
const MODE_SEND      = "send_by_email"

const STEP_TYPE = "type"
const STEP_MODE = "mode"

const TYPE_WITHOUT_LINES = "without_lines"
const TYPE_WITH_LINES    = "with_lines"
const TYPE_PROFITABILITY = "profitability"

const CsvExportModal = ({ isOpen, onClose, csvUrl, ids, count, defaultEmail }) => {
  const [step,       setStep]       = useState(STEP_TYPE)
  const [exportType, setExportType] = useState(TYPE_WITHOUT_LINES)
  const [mode,       setMode]       = useState(MODE_VISUALIZE)
  const [recipient,  setRecipient]  = useState(defaultEmail || "")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setStep(STEP_TYPE)
    setExportType(TYPE_WITHOUT_LINES)
    setMode(MODE_VISUALIZE)
    setRecipient(defaultEmail || "")
  }, [isOpen, defaultEmail])

  const handleSubmit = () => {
    if (submitting) return
    setSubmitting(true)

    const body = {
      ids,
      with_lines:    exportType === TYPE_WITH_LINES,
      profitability: exportType === TYPE_PROFITABILITY,
      export_mode:   mode,
      recipient:     mode === MODE_SEND ? recipient : undefined,
    }

    postRequest({ url: csvUrl, body })
      .then(response => {
        if (!response || response.success === false) {
          flashAlert(I18n.t("quotes.project_index.csv.export_failed"))
          return
        }
        if (response.send_by_email) {
          flashNotice(I18n.t("quotes.project_index.csv.email_pending"))
        } else if (response.visualize_url) {
          window.open(response.visualize_url, "_blank")
        }
        onClose()
      })
      .catch(() => flashAlert(I18n.t("quotes.builder.footer.network_error")))
      .finally(() => setSubmitting(false))
  }

  const TypeOption = ({ value, label, description }) => (
    <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
      <input
        type      = "radio"
        name      = "export_type"
        value     = {value}
        checked   = {exportType === value}
        onChange  = {() => setExportType(value)}
        className = "mt-1"
      />
      <div>
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{description}</div>
      </div>
    </label>
  )

  const footer = step === STEP_TYPE ? (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost"   onClick={onClose}>{I18n.t("button.cancel")}</Button>
        <Button variant="primary" onClick={() => setStep(STEP_MODE)}>
          {I18n.t("quotes.project_index.csv.continue")}
          <FontAwesomeIcon icon="arrow-right" />
        </Button>
      </div>
    </>
  ) : (
    <>
      <Button variant="ghost" onClick={() => setStep(STEP_TYPE)}>
        <FontAwesomeIcon icon="arrow-left" />
        {I18n.t("quotes.project_index.csv.back")}
      </Button>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onClose}>{I18n.t("button.cancel")}</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={submitting || (mode === MODE_SEND && !recipient)}>
          <FontAwesomeIcon icon={submitting ? "spinner" : "file-export"} spin={submitting} />
          {I18n.t("quotes.project_index.csv.run_export")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {I18n.t("quotes.project_index.csv.eyebrow", { step: step === STEP_TYPE ? "1 / 2" : "2 / 2" })}
      title   = {I18n.t("quotes.project_index.csv.title")}
      footer  = {footer}
      size    = "sm"
    >
      <p className="text-sm text-slate-600">
        <span className="font-semibold text-slate-900 tabular-nums">{count}</span>{" "}
        {I18n.t("quotes.project_index.csv.count_to_export", { count })}
      </p>

      {step === STEP_TYPE && (
        <div className="space-y-2">
          <TypeOption
            value       = {TYPE_WITHOUT_LINES}
            label       = {I18n.t("quotes.project_index.csv.without_lines")}
            description = {I18n.t("quotes.project_index.csv.without_lines_description")}
          />
          <TypeOption
            value       = {TYPE_WITH_LINES}
            label       = {I18n.t("quotes.project_index.csv.with_lines")}
            description = {I18n.t("quotes.project_index.csv.with_lines_description")}
          />
          <TypeOption
            value       = {TYPE_PROFITABILITY}
            label       = {I18n.t("quotes.project_index.csv.profitability")}
            description = {I18n.t("quotes.project_index.csv.profitability_description")}
          />
        </div>
      )}

      {step === STEP_MODE && (
        <div className="space-y-2">
          <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type      = "radio"
              name      = "export_mode"
              value     = {MODE_VISUALIZE}
              checked   = {mode === MODE_VISUALIZE}
              onChange  = {() => setMode(MODE_VISUALIZE)}
              className = "mt-1"
            />
            <div>
              <div className="text-sm font-medium text-slate-900">{I18n.t("quotes.project_index.csv.visualize_label")}</div>
              <div className="text-xs text-slate-500 mt-0.5">{I18n.t("quotes.project_index.csv.visualize_description")}</div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type      = "radio"
              name      = "export_mode"
              value     = {MODE_SEND}
              checked   = {mode === MODE_SEND}
              onChange  = {() => setMode(MODE_SEND)}
              className = "mt-1"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900">{I18n.t("quotes.project_index.csv.email_label")}</div>
              <div className="text-xs text-slate-500 mt-0.5">{I18n.t("quotes.project_index.csv.email_description")}</div>
              {mode === MODE_SEND && (
                <input
                  type         = "email"
                  value        = {recipient}
                  onChange     = {event => setRecipient(event.target.value)}
                  placeholder  = {I18n.t("quotes.project_index.csv.email_placeholder")}
                  required
                  autoComplete = "off"
                  className    = "mt-2 w-full h-9 px-3 rounded-md border border-slate-200 text-sm focus:border-brand-500"
                />
              )}
            </div>
          </label>
        </div>
      )}
    </Modal>
  )
}

export default CsvExportModal
